import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6ajckbe24agvuu3hlg";

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

export default function ContactForm() {
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const validate = (name: string, email: string, message: string): FormErrors => {
    const next: FormErrors = {};
    if (!name) next.name = "请填写您的姓名";
    if (!email) next.email = "请填写您的邮箱";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = "邮箱格式不正确，请检查后重试";
    if (!message) next.message = "请填写留言内容";
    else if (message.length > 500) next.message = "留言内容不能超过 500 个字符";
    return next;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim()) payload.append(key, value);
    });

    // 反垃圾：蜜罐被填写则静默按成功处理，不发送任何请求
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setErrors({});
      setStatus("success");
      setFeedback("提交成功！我们已收到您的留言，会尽快与您联系。");
      form.reset();
      return;
    }

    const nextErrors = validate(name, email, message);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setStatus("idle");
      return;
    }

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const data = parsed as
        | { code?: string; message?: string; meta?: { message?: string; detail?: string } }
        | null;
      const serverMsg =
        data?.meta?.message || data?.message || data?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && data?.code === "OK" && !isSpam) {
        setStatus("success");
        setFeedback("提交成功！我们已收到您的留言，会尽快与您联系。");
        form.reset();
        setErrors({});
      } else {
        setStatus("error");
        setFeedback(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后重试。");
    }
  };

  const inputBase =
    "w-full rounded-md bg-background-50 border px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200";

  return (
    <form
      data-readdy-form
      id="contact-form"
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8"
      noValidate
    >
      <h2 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950">在线留言</h2>
      <p className="mt-2 text-sm text-foreground-600">
        填写下方信息给我们留言，我们会尽快回复（本站表单为演示用途，提交后仅作展示反馈）。
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-2">
            姓名 <span className="text-primary-500">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            placeholder="请输入您的姓名"
            className={`${inputBase} ${errors.name ? "border-primary-500" : "border-background-300"}`}
          />
          {errors.name && <p className="mt-1.5 text-xs text-primary-600">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-2">
            邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            placeholder="请输入您的邮箱"
            className={`${inputBase} ${errors.email ? "border-primary-500" : "border-background-300"}`}
          />
          {errors.email && <p className="mt-1.5 text-xs text-primary-600">{errors.email}</p>}
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-2">
          留言内容 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          rows={5}
          maxLength={500}
          placeholder="请输入您想咨询或反馈的内容（最多 500 字）"
          className={`${inputBase} resize-none ${errors.message ? "border-primary-500" : "border-background-300"}`}
        ></textarea>
        {errors.message && <p className="mt-1.5 text-xs text-primary-600">{errors.message}</p>}
      </div>

      {/* 反垃圾字段 */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-guard-field"
      />

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <i className={`${status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} leading-none`}></i>
          {status === "submitting" ? "提交中..." : "提交留言"}
        </button>
        {feedback && (
          <p
            className={`text-sm ${
              status === "success" ? "text-primary-600" : "text-accent-700"
            }`}
          >
            {feedback}
          </p>
        )}
      </div>
    </form>
  );
}